import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { 
  History, 
  X, 
  GitBranch, 
  Calendar, 
  User, 
  Eye, 
  RotateCw,
  ArrowRight,
  ChevronDown,
  ChevronRight,
  Plus,
  Minus,
  AlertCircle
} from 'lucide-react';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, Badge } from '../ui/Table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const PlanVersionHistory = ({ planId, planName, onClose, onCompare }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [versions, setVersions] = useState([]);
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [expandedVersions, setExpandedVersions] = useState(new Set());

  useEffect(() => {
    if (planId) {
      fetchVersionHistory();
    }
  }, [planId]);

  const fetchVersionHistory = async () => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plans/${planId}/versions`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setVersions(data.versions || []);
      } else {
        setError('Failed to load version history');
      }
    } catch (err) {
      console.error('Error fetching version history:', err);
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handleVersionSelect = (versionNumber) => {
    setSelectedVersions(prev => {
      if (prev.includes(versionNumber)) {
        return prev.filter(v => v !== versionNumber);
      } else if (prev.length < 2) {
        return [...prev, versionNumber];
      } else {
        // Replace the first selected version
        return [prev[1], versionNumber];
      }
    });
  };

  const toggleVersionExpansion = (versionNumber) => {
    setExpandedVersions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(versionNumber)) {
        newSet.delete(versionNumber);
      } else {
        newSet.add(versionNumber);
      }
      return newSet;
    });
  };

  const handleCompareVersions = () => {
    if (selectedVersions.length === 2) {
      onCompare(planId, selectedVersions[0], selectedVersions[1]);
    }
  };

  const formatValue = (value) => {
    if (typeof value === 'object' && value !== null) {
      return JSON.stringify(value, null, 2);
    }
    return String(value);
  };

  const getVersionStatusColor = (version) => {
    if (version.is_active) {
      return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
    } else if (version.retired_date) {
      return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
    } else {
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 max-w-4xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-300">Loading version history...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-6xl w-full max-h-screen overflow-y-auto dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <History className="w-6 h-6 text-indigo-600" />
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Version History</h2>
              <p className="text-gray-600 dark:text-gray-300">{planName} - {versions.length} versions</p>
            </div>
          </div>
          <div className="flex gap-2">
            {selectedVersions.length === 2 && (
              <Button onClick={handleCompareVersions} size="sm">
                <GitBranch className="w-4 h-4 mr-2" />
                Compare Versions
              </Button>
            )}
            <Button onClick={onClose} variant="outline" size="sm">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 dark:bg-red-900/10 dark:border-red-800">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-600 mr-2" />
              <p className="text-red-800 dark:text-red-200">{error}</p>
            </div>
          </div>
        )}

        {selectedVersions.length > 0 && (
          <div className="mb-4 p-3 bg-blue-50 rounded-lg dark:bg-blue-900/10">
            <p className="text-blue-800 dark:text-blue-200 text-sm">
              {selectedVersions.length === 1 
                ? `Selected version ${selectedVersions[0]} - select another to compare`
                : `Selected versions ${selectedVersions[0]} and ${selectedVersions[1]} - click "Compare Versions" to view diff`
              }
            </p>
          </div>
        )}

        {/* Version History Table */}
        <div className="bg-white rounded-lg border border-gray-200 dark:bg-gray-900 dark:border-gray-700">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">Select</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Pricing</TableHead>
                <TableHead>Features</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Created By</TableHead>
                <TableHead className="w-24">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {versions.map((version) => (
                <React.Fragment key={version.version}>
                  <TableRow>
                    <TableCell>
                      <input
                        type="checkbox"
                        checked={selectedVersions.includes(version.version)}
                        onChange={() => handleVersionSelect(version.version)}
                        className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        disabled={selectedVersions.length >= 2 && !selectedVersions.includes(version.version)}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          v{version.version}
                        </span>
                        {version.is_active && (
                          <Badge variant="success">Current</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant={version.is_active ? 'success' : version.retired_date ? 'error' : 'default'}>
                        {version.is_active ? 'Active' : version.retired_date ? 'Retired' : 'Inactive'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium text-gray-900 dark:text-gray-100">
                          ${version.pricing?.monthly || 0}/month
                        </div>
                        <div className="text-gray-500 dark:text-gray-400">
                          ${version.pricing?.annual || 0}/year
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {version.features?.length || 0} features
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {new Date(version.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-gray-600 dark:text-gray-300">
                        {version.created_by || 'System'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => toggleVersionExpansion(version.version)}
                        >
                          {expandedVersions.has(version.version) ? (
                            <ChevronDown className="w-4 h-4" />
                          ) : (
                            <ChevronRight className="w-4 h-4" />
                          )}
                        </Button>
                        <Button variant="outline" size="sm">
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Version Details */}
                  {expandedVersions.has(version.version) && (
                    <TableRow>
                      <TableCell colSpan={8}>
                        <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Pricing Details */}
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Pricing</h4>
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Monthly:</span>
                                  <span className="font-medium">${version.pricing?.monthly || 0}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-gray-600 dark:text-gray-400">Annual:</span>
                                  <span className="font-medium">${version.pricing?.annual || 0}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Allocations */}
                            <div>
                              <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Allocations</h4>
                              <div className="space-y-2 text-sm">
                                {Object.entries(version.included_allocations || {}).map(([key, value]) => (
                                  <div key={key} className="flex justify-between">
                                    <span className="text-gray-600 dark:text-gray-400 capitalize">
                                      {key.replace(/_/g, ' ')}:
                                    </span>
                                    <span className="font-medium">{value === -1 ? 'Unlimited' : value}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            {/* Features */}
                            {version.features && version.features.length > 0 && (
                              <div className="lg:col-span-2">
                                <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">Features</h4>
                                <div className="flex flex-wrap gap-2">
                                  {version.features.map(feature => (
                                    <Badge key={feature} variant="info">
                                      {feature.replace(/_/g, ' ')}
                                    </Badge>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {versions.length === 0 && (
          <div className="text-center py-12">
            <History className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-sm font-medium text-gray-900 mb-1 dark:text-gray-100">No version history</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">Version history will appear here when plan changes are made</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanVersionHistory;